import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, DollarSign, AlertCircle, CreditCard, Smartphone, Building } from 'lucide-react';
import toast from 'react-hot-toast';

const FundingRequestModal = ({ onClose, onSubmit, isLoading }) => {
  const [selectedMethod, setSelectedMethod] = useState('bank_transfer');
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const amount = watch('amount');

  const paymentMethods = [
    {
      id: 'bank_transfer',
      name: 'Bank Transfer',
      icon: Building,
      description: 'Direct bank transfer from your account',
      placeholder: 'e.g., CBE123456789',
    },
    {
      id: 'mobile_money',
      name: 'Mobile Money',
      icon: Smartphone,
      description: 'Mobile money services (Telebirr, M-Pesa, etc.)',
      placeholder: 'e.g., 0911123456',
    },
    {
      id: 'cash',
      name: 'Cash Deposit',
      icon: DollarSign,
      description: 'Cash deposit at our office',
      placeholder: 'e.g., Cash deposit at Addis Ababa office',
    },
    {
      id: 'other',
      name: 'Other',
      icon: CreditCard,
      description: 'Other payment methods',
      placeholder: 'Describe your payment method',
    },
  ];

  const selectedMethodData = paymentMethods.find(method => method.id === selectedMethod);

  const onFormSubmit = (data) => {
    const requestData = {
      amount: parseFloat(data.amount),
      method: selectedMethod,
      reference: data.reference,
    };

    onSubmit(requestData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Add Funds</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (ETB) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                {...register('amount', {
                  required: 'Amount is required',
                  min: { value: 10, message: 'Minimum amount is 10 ETB' },
                  max: { value: 50000, message: 'Maximum amount is 50,000 ETB' },
                })}
                type="number"
                step="0.01"
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter amount"
              />
            </div>
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
            )}
            {amount && (
              <p className="mt-1 text-sm text-gray-500">
                Processing fee: 0 ETB (Free for wallet funding)
              </p>
            )}
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Payment Method *
            </label>
            <div className="space-y-3">
              {paymentMethods.map((method) => {
                const Icon = method.icon;
                return (
                  <div
                    key={method.id}
                    className={`relative border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedMethod === method.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedMethod(method.id)}
                  >
                    <div className="flex items-start">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          value={method.id}
                          checked={selectedMethod === method.id}
                          onChange={() => setSelectedMethod(method.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <Icon className="ml-3 h-5 w-5 text-gray-600" />
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-gray-900">
                          {method.name}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {method.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaction Reference *
            </label>
            <input
              {...register('reference', {
                required: 'Transaction reference is required',
                minLength: { value: 3, message: 'Reference must be at least 3 characters' },
              })}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={selectedMethodData?.placeholder}
            />
            {errors.reference && (
              <p className="mt-1 text-sm text-red-600">{errors.reference.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Enter your transaction reference number or payment details. Must be unique for each pending request.
            </p>
          </div>

          {/* Important Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Important Information:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Minimum funding amount: 10 ETB</li>
                  <li>Maximum funding amount: 50,000 ETB</li>
                  <li>Processing time: 1-24 hours</li>
                  <li>Keep your transaction reference for verification</li>
                  <li>All funding requests are subject to admin approval</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Anti-Fraud Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Anti-Fraud Notice:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Maximum 3 pending funding requests at a time</li>
                  <li>Rejected requests require 24-hour cooldown</li>
                  <li>False references will result in account suspension</li>
                  <li>Admin may request additional verification</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FundingRequestModal;
